/**
 * Credentialed JSON API facade for planner activities, scheduling, integrations, state, workspace,
 * teams, and authentication. Mutations send a CSRF marker and a sessionStorage-backed idempotency
 * key derived from identity, method, route, and body. Network and HTTP failures become response
 * envelopes.
 *
 * @module apps/web/src/lib/api
 */

import { taskToUI } from '@fluxability/types';
import { captureTaskSnapshot, isTaskSnapshotCurrent, useTasksStore } from '@/stores/tasksStore';

import type {
  ApplicationReceipt,
  ApplyValueInput,
  ReorganizeReceipt,
  StructureOperation,
  StructureResponse,
  UndoReceipt,
  UserValue,
  ValueInput,
  ValueLink,
  ValueLinkInput,
  ValueSuggestions,
} from '@fluxability/types';
import type {
  AccountResponse,
  ActivitySessionInput,
  ActivitySessionResult,
  SavedActivitySessionsResult,
  CorrectActivitySessionInput,
  CorrectActivitySessionResult,
  AttributeDef,
  AttributeInput,
  CalendarEventRecord,
  CalendarEventUpdate,
  CalendarListItem,
  CalendarSelectionResult,
  CalendarStatus,
  CalendarSyncResult,
  CapacityAnalysis,
  ConstraintInput,
  ConstraintOverrideState,
  CreateSleepLogInput,
  CreateStateCheckInInput,
  CreateWaterLogInput,
  DeletedRecord,
  EmailLinkResult,
  EmailList,
  EmailVerifyPreview,
  EmailVerifyResult,
  EnergyPatternBucket,
  FeedbackInput,
  FeedbackReceipt,
  GhostBlocksData,
  GhostConfirmResult,
  ImmersionCancelResult,
  ImmersionPauseResult,
  ImmersionSessionView,
  ImmersionStartResult,
  InviteDelivery,
  JoinRuleInput,
  MailAccountRequest,
  MailProvisioningOutcome,
  MemberDecisionInput,
  MemberInviteInput,
  NextDayScheduleResult,
  NlpCommandResult,
  NlpSuggestions,
  OAuthProviders,
  OcrCreateTasksResult,
  OcrHistoryEntry,
  OcrScanResult,
  OnboardingInput,
  PendingEmail,
  ProfileUpdate,
  RolloverResult,
  ScheduleCommitment,
  ScheduleResult,
  SchedulingConstraint,
  SettingsResponse,
  SettingsUpdate,
  SleepLog,
  StateCheckIn,
  StateTrajectory,
  TaskCreateInput,
  TaskDeleteResult,
  TaskDocument,
  TaskInferenceResult,
  TaskListResponse,
  TaskPatch,
  TaskResponse,
  TeamActivityInput,
  TeamBundleInput,
  TeamConnectionInput,
  TeamCustomField,
  TeamDiscoveryResult,
  TeamEmailRule,
  TeamEmailRuleInput,
  TeamExternalConnection,
  TeamFeed,
  TeamFeedInput,
  TeamInput,
  TeamInviteRequest,
  TeamJoinRequest,
  TeamJoinRule,
  TeamMember,
  TeamProjectInput,
  TeamWithMembership,
  TeamWorkspaceData,
  WaterLog,
  WorkspaceBundle,
  WorkspaceBundleInput,
  WorkspaceData,
  WorkspaceProject,
  WorkspaceProjectInput,
} from '@fluxability/types';
import type { BrowserScheduleProposal, BrowserScheduleStatus } from './browserScheduleFlow';
import { readBrowserGhostBlocks } from './browserGhostBlocks';

/**
 * Configured API root normalized to include /api, defaulting to same-origin /api.
 */

export const API_BASE = normalizeApiBase(import.meta.env.VITE_API_URL);

/** Normalize the build-time API root, supplying /api for empty or root-only origins. Relative paths gain an /api suffix; explicit absolute subpaths are preserved. Invalid absolute URLs throw during module initialization. */
function normalizeApiBase(value?: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '/api';

  const trimmed = raw.replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
  }

  const url = new URL(trimmed);
  if (!url.pathname || url.pathname === '/') {
    url.pathname = '/api';
  }
  return url.toString().replace(/\/+$/, '');
}

/** Normalized transport result. success distinguishes application failure from data absence; status is available for HTTP failures, while network failures carry an error without an HTTP status. */
export interface ApiResponse<T> {
  /** HTTP status available on unsuccessful HTTP responses; absent for transport-only failures. */
  status?: number;
  /** Whether the normalized response reports application success. */
  success: boolean;
  /** Successful response payload; a 204 response can succeed without data. */
  data?: T;
  /** Failure details from the server or local transport normalization. */
  error?: {
    /** Machine-readable server or transport failure code. */
    code: string;
    /** User-facing failure explanation, which must not be treated as an authorization decision. */
    message: string;
  };
}

/**
 * Send a JSON-oriented API request with cookies and any retained legacy bearer token. Mutations reuse an idempotency key after network/server uncertainty and clear it after success or 4xx responses. Normalizes raw and enveloped responses, handles 204, and reports timeout/network failures without clearing the session. Storage and request-body preparation occur before the fetch error handler.
 */
export async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const token = localStorage.getItem('flexiplanner-session-token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const mutation = !['GET', 'HEAD', 'OPTIONS'].includes((options.method || 'GET').toUpperCase());
  let mutationKey: string | undefined;
  if (mutation) {
    headers['X-Fluxability-CSRF'] = '1';
    const bytes =
      options.body && typeof options.body !== 'string'
        ? await new Response(options.body).arrayBuffer()
        : new TextEncoder().encode(String(options.body || ''));
    const bodyDigest = await crypto.subtle.digest('SHA-256', bytes);
    const bodyHash = Array.from(new Uint8Array(bodyDigest), byte =>
      byte.toString(16).padStart(2, '0')
    ).join('');
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(`${token || 'cookie'}:${options.method}:${endpoint}:${bodyHash}`)
    );
    mutationKey = `fluxability-mutation:${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')}`;
    const previous = sessionStorage.getItem(mutationKey);
    headers['Idempotency-Key'] = previous || crypto.randomUUID();
    sessionStorage.setItem(mutationKey, headers['Idempotency-Key']);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (mutationKey && (response.ok || (response.status >= 400 && response.status < 500)))
      sessionStorage.removeItem(mutationKey);
    if (response.status === 204) return { success: true };
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        error: data?.error || { code: 'UNKNOWN_ERROR', message: 'An error occurred' },
      };
    }

    return typeof data?.success === 'boolean' ? data : { success: true, data: data?.data ?? data };
  } catch (error) {
    return {
      success: false,
      error: {
        code: options.signal?.reason?.name === 'TimeoutError' ? 'REQUEST_TIMEOUT' : 'NETWORK_ERROR',
        message:
          options.signal?.reason?.name === 'TimeoutError'
            ? 'The server is taking too long to respond. Please retry; your session has been kept so we can confirm its status.'
            : error instanceof Error
              ? error.message
              : 'Network error',
      },
    };
  }
}

/** Bind committed related records to the current account's UI store. Rust selects the affected
 * canonical records; this binding applies only those records, without replacing an inventory.
 * A newer local mutation or account switch fences delayed responses; realtime handles its own
 * ordered per-record delivery through the existing socket binding. */
async function activityMutationRequest(path: string, options: RequestInit): Promise<ApiResponse<TaskResponse>> {
  const snapshot = captureTaskSnapshot();
  const response = await request<TaskResponse>(path, options);
  if (response.success && snapshot.userId && isTaskSnapshotCurrent(snapshot)) {
    const related = response.data?.related_activities ?? [];
    useTasksStore.getState().upsertTasks(
      related.filter(task => task.user_id === snapshot.userId).map(taskToUI), 'realtime'
    );
  }
  return response;
}

// Activities API (backed by the legacy /tasks route)
/**
 * Activity CRUD, inference, reorder, and batch deletion methods on the continuity /tasks routes.
 */
export const tasksApi = {
  /** Load activity records for the current session through the retained /tasks endpoint. */
  list: () => request<TaskListResponse | TaskDocument[]>('/tasks'),
  /** Load one activity by its stored identifier. */
  get: (id: string) => request<TaskResponse>(`/tasks/${id}`),
  /** Submit a new activity; inspect the returned success envelope before projecting it locally. */
  create: (task: TaskCreateInput) =>
    activityMutationRequest('/tasks', { method: 'POST', body: JSON.stringify(task) }),
  /** Request server planning-field inference for selected activity IDs, optionally applying results. */
  inferPlanning: (payload: { task_ids: string[]; apply?: boolean; limit?: number }) =>
    request<TaskInferenceResult>('/tasks/infer', { method: 'POST', body: JSON.stringify(payload) }),
  /** Patch an activity's mutable fields; caller owns local-store reconciliation. */
  update: (id: string, updates: TaskPatch) =>
    activityMutationRequest(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  /** Delete one activity; resolved requests can still contain an unsuccessful envelope. */
  delete: (id: string) => request<TaskDeleteResult>(`/tasks/${id}`, { method: 'DELETE' }),
  /** Delete selected activities and return separate deleted-ID and failure lists. */
  batchDelete: (taskIds: string[]) =>
    request<TaskDeleteResult>('/tasks/batch-delete', {
      method: 'POST',
      body: JSON.stringify({ task_ids: taskIds }),
    }),
  /** Persist sibling position and optional parent after a drag or hierarchy edit. */
  reorder: (taskId: string, position: number, parentId?: string) =>
    request<void>(`/tasks/${taskId}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ position, parent_id: parentId }),
    }),
};

// Schedule API
/**
 * Scheduling, rollover, ghost confirmation, feasibility, constraint overrides, and asynchronous solve status/cancellation endpoints.
 */
export const scheduleApi = {
  /** Read placements without triggering the legacy server-side rollover or optimizer. */
  get: (start: string, end: string) =>
    request<{
      /** Activities scheduled in the range. */ scheduled_tasks: TaskDocument[];
      /** Calendar commitments in the range. */ gcal_events: ScheduleCommitment[];
      /** Requested range start, echoed. */ start: string;
      /** Requested range end, echoed. */ end: string;
    }>(
      `/schedule?execution=browser&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
    ),
  /** Fetch authorized calendar inputs, then compute exact ghost suggestions in a separate browser Worker. */
  getGhostBlocks: (weekStart?: string) =>
    readBrowserGhostBlocks(() =>
      request<GhostBlocksData>(
        `/schedule/ghost-blocks?execution=browser${weekStart ? `&week_start=${encodeURIComponent(weekStart)}` : ''}`
      )
    ),
  /** Load capacity analysis for the optional planning week. */
  checkFeasibility: (weekStart?: string) =>
    request<CapacityAnalysis>(
      `/schedule/feasibility${weekStart ? `?week_start=${weekStart}` : ''}`
    ),
  /** Request placement of one activity, optionally around a target timing expression. */
  scheduleTask: (taskId: string, targetTime?: string) =>
    request<ScheduleResult>(`/schedule/task/${taskId}`, {
      method: 'POST',
      body: JSON.stringify({ target_time: targetTime }),
    }),
  /** Move the activity through the server's next-day scheduling operation. */
  scheduleTaskTomorrow: (taskId: string) =>
    request<NextDayScheduleResult>(`/schedule/task/${taskId}/next-day`, { method: 'POST' }),
  /** Accept a stored placement suggestion by its ghost identifier. */
  confirmGhostBlock: (ghostBlockId: string) =>
    request<GhostConfirmResult>(`/schedule/ghost-block/${ghostBlockId}/confirm`, {
      method: 'POST',
    }),
  /** Capture a browser scheduling lease by default; server computation is an explicit fallback. */
  autoSchedule: (
    weekStart?: string,
    execution: 'browser' | 'server' = 'browser',
    signal?: AbortSignal,
    rolloverOnly = false
  ) =>
    request<BrowserScheduleStatus>('/schedule/auto', {
      method: 'POST',
      body: JSON.stringify({ week_start: weekStart, execution, rollover_only: rolloverOnly }),
      signal,
    }),
  /** Submit scheduling-only intervals; the server validates and returns committed documents. */
  proposeSchedule: (proposal: BrowserScheduleProposal, signal?: AbortSignal) =>
    request<BrowserScheduleStatus>('/schedule/auto/proposal', {
      method: 'POST',
      body: JSON.stringify(proposal),
      signal,
    }),
  /** Read the current asynchronous scheduling operation's status. */
  autoScheduleStatus: () => request<BrowserScheduleStatus>('/schedule/auto/status'),
  /** Cancel only the named job when supplied, protecting a newer solve from late cleanup. */
  cancelAutoSchedule: (jobId?: string) =>
    request<BrowserScheduleStatus>('/schedule/auto/cancel', {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId }),
    }),
  /** Run the server operation that carries unfinished work forward. */
  rollover: () => request<RolloverResult>('/schedule/rollover', { method: 'POST' }),
  /** Read whether exceptional constraint handling is enabled for the optional week. */
  getConstraintOverride: (weekStart?: string) =>
    request<ConstraintOverrideState>(
      `/schedule/constraint-override${weekStart ? `?week_start=${weekStart}` : ''}`
    ),
  /** Set exceptional constraint handling for a specific planning week. */
  setConstraintOverride: (weekStart: string, enabled: boolean) =>
    request<ConstraintOverrideState>('/schedule/constraint-override', {
      method: 'POST',
      body: JSON.stringify({ week_start: weekStart, enabled }),
    }),
};

// NLP API
/**
 * Natural-language command API used by the command palette and calendar free-time actions.
 */
export const nlpApi = {
  /** Submit planning text with browser timezone and offset for contextual interpretation. */
  command: (command: string) =>
    request<NlpCommandResult>('/nlp/command', {
      method: 'POST',
      body: JSON.stringify({
        command,
        timezoneOffset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    }),
  /** Fetch encoded-query suggestions for the command palette. */
  suggestions: (q: string) =>
    request<NlpSuggestions>(`/nlp/suggestions?q=${encodeURIComponent(q)}`),
};

// Calendar API (Google Calendar)
/**
 * Google Calendar authorization, connection status, calendar selection, event metadata, sync, and revocation methods.
 */
export const calendarApi = {
  /** Request the Google authorization URL; validate it before browser navigation. */
  getAuthUrl: () => request<{ url: string }>('/calendar/auth/url'),
  /** Read provider connection and synchronization status for the current account. */
  getStatus: () => request<CalendarStatus>('/calendar/status'),
  /** Load provider calendars available to the connected account. */
  listCalendars: () => request<CalendarListItem[]>('/calendar/calendars'),
  /** Persist the selected provider calendar IDs used for synchronization. */
  updateCalendars: (calendarIds: string[]) =>
    request<CalendarSelectionResult>('/calendar/calendars', {
      method: 'PUT',
      body: JSON.stringify({ calendarIds }),
    }),
  /** Request a provider synchronization pass and inspect the returned outcome. */
  sync: () => request<CalendarSyncResult>('/calendar/sync', { method: 'POST' }),
  /** Load calendar commitments, optionally bounded by encoded start/end query parameters. */
  getEvents: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request<CalendarEventRecord[]>(`/calendar/events?${params}`);
  },
  /** Patch commitment metadata using an encoded provider event identifier. */
  updateEvent: (id: string, updates: CalendarEventUpdate) =>
    request<CalendarEventRecord>(`/calendar/events/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  /** Revoke the current account's calendar authorization. */
  revoke: () => request<void>('/calendar/auth/revoke', { method: 'POST' }),
};

// OCR API
/**
 * Image scanning request used to extract reviewable activity suggestions.
 */
export const ocrApi = {
  /** Upload an image as multipart form data with session credentials; returns parsed JSON directly, without request-envelope normalization. */
  scan: (file: File): Promise<ApiResponse<OcrScanResult>> => {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('flexiplanner-session-token');
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${API_BASE}/ocr/scan`, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
    }).then(r => r.json());
  },
  /** Create activities from a stored scan, optionally assigning a schedule date. */
  createTasks: (scanId: string, scheduleDate?: string) =>
    request<OcrCreateTasksResult>('/ocr/create-tasks', {
      method: 'POST',
      body: JSON.stringify({ scanId, scheduleDate }),
    }),
  /** Load the current account's previous OCR scans. */
  getHistory: () => request<OcrHistoryEntry[]>('/ocr/history'),
};

/**
 * One custom field's evidence coverage in `modelError.customFields.usage`. The current server
 * (`training_runtime/service.rs`) sends `attrId`, `scope`, `specificity`, `weight` and
 * `observedValueCount`; the other fields are read when present and are otherwise absent.
 */
export interface CustomFieldUsageRow {
  /** Field identifier. */
  attrId?: string;
  /** `personal` or `team`. */
  scope?: string;
  /** Field specificity. */
  specificity?: number;
  /** Field weight. */
  weight?: number;
  /** Training observations that carry a value for the field. */
  observedValueCount?: number;
  /** Field label, when sent. */
  name?: string;
  /** Activities eligible to carry the field, when sent. */
  eligibleTaskCount?: number;
  /** Observations whose value is numeric, when sent. */
  numericValueCount?: number;
  /** Owning team's name for team fields, when sent. */
  teamName?: string;
  /** Field value type, when sent. */
  dataType?: string;
}

/**
 * Analytics response fields for learning progress, prediction errors, sample coverage, and per-field/model diagnostics.
 */
export interface TrainingAnalyticsData {
  /** Optional aggregate feedback, session, completion, and scheduling statistics. */
  overview?: {
    /** Number of feedback records included in the server summary. */
    totalFeedback?: number;
    /** Number of immersion sessions included in the server summary. */
    totalSessions?: number;
    /** Mean user feedback rating when ratings are available. */
    averageRating?: number;
    /** Server-reported completion rate used in the overview display. */
    completionRate?: number;
    /** Server-reported scheduling accuracy summary when available. */
    schedulingAccuracy?: number;
  };
  /** Aggregate energy level and recurring time-of-week observations. */
  energy?: {
    /** Average reported energy level across included observations. */
    averageLevel?: number;
    /** Energy observations grouped by weekday and hour. */
    patterns?: Array<{
      /** Weekday index associated with an energy-pattern bucket. */
      day_of_week?: number;
      /** Local hour associated with an energy-pattern bucket. */
      hour_of_day?: number;
      /** Mean energy observed in this time bucket. */
      avg_energy?: number;
      /** Number of observations supporting this energy-pattern bucket. */
      samples?: number;
    }>;
  };
  /** Aggregate reported focus measurements. */
  focus?: {
    /** Average focus level when this response representation is supplied. */
    averageLevel?: number;
    /** Alternate aggregate focus score returned by the analytics endpoint. */
    averageScore?: number;
  };
  /** Aggregate predicted-versus-observed duration summary. */
  duration?: {
    /** Server-reported observed duration divided by predicted duration; 1 means equal durations. */
    averageRatio?: number;
    /** Qualitative description of change in duration prediction accuracy. */
    accuracyTrend?: string;
  };
  /** Activity counts grouped by lifecycle status. */
  taskStatus?: Array<{
    /** Lifecycle value identifying this activity-count bucket. */ status?: string;
    /** Number of records in this distribution bucket. */ count?: number;
  }>;
  /** Feedback records grouped by feedback category. */
  feedbackDistribution?: Array<{
    /** Feedback category identifying this distribution bucket. */ feedback_type?: string;
    /** Number of records in this distribution bucket. */ count?: number;
  }>;
  /** Measured prediction errors and custom-field evidence diagnostics. */
  modelError?: {
    /** Duration error statistics computed from paired prediction/outcome evidence. */
    duration?: {
      /** Number of paired observations supporting the duration error statistics. */
      sampleCount?: number;
      /** Mean absolute duration prediction error in minutes. */
      maeMinutes?: number;
      /** Root mean squared duration prediction error in minutes. */
      rmseMinutes?: number;
      /** Mean signed duration error in minutes; preserves systematic bias. */
      meanErrorMinutes?: number;
      /** Server-reported observed duration divided by predicted duration; 1 means equal durations. */
      averageRatio?: number;
      /** Duration-error aggregates grouped by activity class. */
      byClass?: Array<Record<string, unknown>>;
      /** Time-bucketed duration-error observations returned by the server. */
      trend?: Array<Record<string, unknown>>;
    };
    /** Custom-field coverage and change diagnostics accompanying model errors. */
    customFields?: {
      /** Aggregate custom-field diagnostic values in the server-defined shape. */
      summary?: Record<string, unknown>;
      /** Per-field usage and observed-value coverage records. */
      usage?: CustomFieldUsageRow[];
      /** Server-reported custom-field change summary. */
      changes?: Record<string, unknown>;
    };
    /** Explanatory notes qualifying interpretation of the model diagnostics. */
    notes?: string[];
  };
  /** Counts describing which evidence is available for personalization. */
  trainingDataShape?: {
    /** Number of activity records represented in the training-data shape. */
    tasks?: number;
    /** Number of feedback observations available for training. */
    feedbackSamples?: number;
    /** Number of immersion sessions represented in the evidence summary. */
    sessions?: number;
    /** Number of duration observations available for learning. */
    durationSamples?: number;
    /** Number of energy observations available for learning. */
    energySamples?: number;
    /** Number of canonical training samples available. */
    trainingSamples?: number;
    /** Number of custom field definitions represented in training metadata. */
    customFields?: number;
    /** Server-defined custom-field coverage grouped by ownership scope. */
    customFieldsByScope?: {
      /** Count of personal-scope custom-field definitions. */
      personal?: number;
      /** Count of team-scope custom-field definitions. */
      team?: number;
      /** Count of custom-field definitions marked required. */
      required?: number;
      /** Count of custom fields represented in activity data. */
      usedInActivities?: number;
      /** Count of observed custom-field values in the evidence summary. */
      observedValues?: number;
      /** Number of teams represented by scoped custom-field evidence. */
      teams?: number;
    };
  };
  /** Additional server-defined learning-progress and improvement diagnostics. */
  modelImprovement?: Record<string, unknown>;
  /** Server-defined summary of additions or changes to training evidence. */
  trainingDataChanges?: Record<string, unknown>;
  /** Server-defined custom-field coverage grouped by ownership scope. */
  customFieldsByScope?: Record<string, unknown>;
}

/**
 * Exported training dataset structure containing activity history and related model/state metadata.
 */
export interface TrainingExportData {
  /** Timestamp when the server assembled the export. */
  exportedAt?: string;
  /** Owner account whose personal training dataset was exported. */
  userId?: string;
  /** Version identifying the export's record layout. */
  schemaVersion?: string;
  /** Raw feedback records included when available. */
  feedback?: Array<Record<string, unknown>>;
  /** Recorded immersion sessions used as activity outcome evidence. */
  immersionSessions?: Array<Record<string, unknown>>;
  /** Paired duration observations used by duration learning. */
  durationTrainingData?: Array<Record<string, unknown>>;
  /** Recorded duration adjustments or differences included in the export. */
  durationDeltas?: Array<Record<string, unknown>>;
  /** Time-context energy observations or aggregates included in the export. */
  energyPatterns?: Array<Record<string, unknown>>;
  /** Activity snapshots included to contextualize training observations. */
  tasks?: Array<Record<string, unknown>>;
  /** Custom-field definitions needed to interpret exported field values. */
  attributes?: Array<Record<string, unknown>>;
  /** Project taxonomy records referenced by exported activities. */
  projects?: Array<Record<string, unknown>>;
  /** Bundle taxonomy records referenced by exported activities. */
  bundles?: Array<Record<string, unknown>>;
  /** Canonical model-training samples included in the dataset. */
  trainingSamples?: Array<Record<string, unknown>>;
  /** Recorded work intervals contributing to observed activity duration. */
  workSegments?: Array<Record<string, unknown>>;
  /** Detailed session events included when raw evidence is requested. */
  immersionEvents?: Array<Record<string, unknown>>;
}

/**
 * Training export request options selecting JSON/CSV and whether raw records are included.
 */
export interface TrainingExportPayload {
  /** Requested JSON or CSV serialization format. */
  format?: 'json' | 'csv';
  /** Whether the export should include detailed raw evidence records. */
  includeRaw?: boolean;
}

// Training API
/**
 * Immersion lifecycle, retrospective sessions, feedback, analytics, and history-export methods. Session starts support explicit operation keys for safe retry after uncertain responses.
 */
export const trainingApi = {
  /** Store user-confirmed activity feedback as training evidence. */
  submitFeedback: (feedback: FeedbackInput) =>
    request<FeedbackReceipt>('/training/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    }),
  /** Start or restore a session with an optional operation key; a ten-second timeout can leave commit status uncertain. A `concurrentWithSessionId` runs it alongside that active session without ending it. */
  startImmersion: (
    taskId: string,
    estimatedDuration: number,
    startNow = false,
    idempotencyKey?: string,
    concurrentWithSessionId?: string
  ) =>
    request<ImmersionStartResult>('/training/immersion/start', {
      method: 'POST',
      body: JSON.stringify({ taskId, estimatedDuration, startNow, idempotencyKey, concurrentWithSessionId }),
      signal: AbortSignal.timeout(10_000),
    }),
  /** Record a session pause with duration and optional reason. */
  pauseImmersion: (sessionId: string, duration: number, reason?: string, transition?: { action: 'pause' | 'resume'; occurredAt: string; idempotencyKey: string }) =>
    request<ImmersionPauseResult>(`/training/immersion/${sessionId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ duration, reason, ...transition }),
    }),
  /** Request session cancellation with a ten-second timeout; callers must resolve uncertain outcomes before retrying other mutations. */
  cancelImmersion: (sessionId: string) =>
    request<ImmersionCancelResult>(`/training/immersion/${sessionId}/cancel`, {
      method: 'POST',
      signal: AbortSignal.timeout(10_000),
    }),
  /** Record a session outcome and optional detailed observations; detail fields are merged into the submitted body. */
  endImmersion: (
    sessionId: string,
    completed: boolean,
    energyLevel?: number,
    details?: ActivitySessionInput
  ) =>
    request<ActivitySessionResult>(`/training/immersion/${sessionId}/end`, {
      method: 'POST',
      body: JSON.stringify({ completed, energyLevel, ...details }),
    }),
  /** Record a retrospective activity session from the supplied observations. */
  logActivitySession: (payload: ActivitySessionInput) =>
    request<ActivitySessionResult>('/training/activity-session', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Binding for owner-fenced saved-session history; Rust supplies validated projections. */
  getActivitySessions: (taskId: string, expectedOwnerId: string) =>
    request<SavedActivitySessionsResult>(`/training/activity-sessions?${new URLSearchParams({ taskId, expectedOwnerId, limit: '100' })}`),
  /** Binding for a revision-checked correction; Rust owns validation and evidence repair. */
  correctActivitySession: (sessionId: string, payload: CorrectActivitySessionInput) =>
    request<CorrectActivitySessionResult>(`/training/activity-sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  /** Load the account's active session using the single-session endpoint. */
  getActiveImmersion: () => request<ImmersionSessionView | null>('/training/immersion/active'),
  /** Load all active sessions with a ten-second request timeout. */
  getActiveImmersions: () =>
    request<ImmersionSessionView[]>('/training/immersion/active/all', {
      signal: AbortSignal.timeout(10_000),
    }),
  /** Load stored activity feedback records. */
  getFeedback: () =>
    request<{
      /** Stored feedback rows, unchecked. */ feedback: Record<string, unknown>[];
      /** Page window over all rows. */ pagination: {
        /** Total rows. */ total: number;
        /** Page size. */ limit: number;
        /** Page offset. */ offset: number;
      };
    }>('/training/feedback'),
  /** Load aggregate learning evidence, prediction errors, and field-coverage diagnostics. */
  getAnalytics: () => request<TrainingAnalyticsData>('/training/analytics'),
  /** Load time-context energy observations used in analytics. */
  getEnergyPatterns: () =>
    request<{
      /** Weekday/hour energy buckets. */ patterns: EnergyPatternBucket[];
      /** Mean energy by weekday (rows, Sunday = 0) and hour (columns), null without samples. */
      matrix: (number | null)[][];
      /** Up to ten highest-energy buckets. */ peakHours: EnergyPatternBucket[];
      /** Scheduling recommendations. */ recommendations: string[];
    }>('/training/energy-patterns'),
  /** Request JSON or CSV training-data export with optional raw evidence. */
  exportTrainingData: (payload: TrainingExportPayload = {}) =>
    request<TrainingExportData | string>('/training/export', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// State trajectory API
/**
 * Hydration, sleep, state check-in, and trajectory API methods.
 */
export const stateApi = {
  /** Load the combined state summary with optional encoded window bounds. */
  getTrajectory: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request<StateTrajectory>(`/state/trajectory${params.toString() ? `?${params}` : ''}`);
  },
  /** Load water observations with optional encoded window bounds. */
  listWater: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request<WaterLog[]>(`/state/water${params.toString() ? `?${params}` : ''}`);
  },
  /** Persist a hydration observation for the current account. */
  logWater: (payload: CreateWaterLogInput) =>
    request<Pick<WaterLog, 'id' | 'amount' | 'unit' | 'amount_ml' | 'occurred_at'>>(
      '/state/water',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    ),
  /** Load recent sleep observations, defaulting to at most thirty records. */
  listSleep: (limit = 30) => request<SleepLog[]>(`/state/sleep?limit=${limit}`),
  /** Persist a sleep interval and optional waking-state observations. */
  logSleep: (payload: CreateSleepLogInput) =>
    request<Pick<SleepLog, 'id' | 'duration_minutes' | 'occurred_at'>>('/state/sleep', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Load recent subjective check-ins, defaulting to at most fifty records. */
  listCheckIns: (limit = 50) => request<StateCheckIn[]>(`/state/check-ins?limit=${limit}`),
  /** Persist a subjective state observation and optional context. */
  logCheckIn: (payload: CreateStateCheckInInput) =>
    request<Pick<StateCheckIn, 'id' | 'occurred_at'>>('/state/check-ins', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

/**
 * Personal project and bundle CRUD plus aggregate workspace loading.
 */
export const workspaceApi = {
  /** Load personal project/bundle taxonomy and the default bundle. */
  get: () => request<WorkspaceData>('/workspace'),
  /** Create a personal workspace project from the supplied draft. */
  createProject: (project: WorkspaceProjectInput) =>
    request<{ /** The created project. */ project: WorkspaceProject }>('/workspace/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    }),
  /** Patch one personal project; callers reconcile the returned record into local state. */
  updateProject: (id: string, project: Partial<WorkspaceProjectInput>) =>
    request<{ /** The updated project. */ project: WorkspaceProject }>(
      `/workspace/projects/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(project),
      }
    ),
  /** Request deletion of a personal project by identifier. */
  deleteProject: (id: string) =>
    request<DeletedRecord>(`/workspace/projects/${id}`, { method: 'DELETE' }),
  /** Create a personal activity grouping from the supplied draft. */
  createBundle: (bundle: WorkspaceBundleInput) =>
    request<{ /** The created bundle. */ bundle: WorkspaceBundle }>('/workspace/bundles', {
      method: 'POST',
      body: JSON.stringify(bundle),
    }),
  /** Patch one personal bundle; callers reconcile the returned record into local state. */
  updateBundle: (id: string, bundle: Partial<WorkspaceBundleInput>) =>
    request<{ /** The updated bundle. */ bundle: WorkspaceBundle }>(`/workspace/bundles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(bundle),
    }),
  /** Request deletion of a personal bundle by identifier. */
  deleteBundle: (id: string) =>
    request<DeletedRecord>(`/workspace/bundles/${id}`, { method: 'DELETE' }),
  /** Ask what merging bundle `id` into `intoBundleId` would do, without changing anything. */
  previewBundleMerge: (id: string, intoBundleId: string) =>
    request<{ /** Server-computed merge preview. */ preview: BundleMergePreview }>(
      `/workspace/bundles/${id}/merge`,
      { method: 'POST', body: JSON.stringify({ intoBundleId, dryRun: true }) }
    ),
  /** Move every activity of bundle `id` into `intoBundleId` and archive `id`; returns the refreshed workspace. */
  mergeBundle: (id: string, intoBundleId: string) =>
    request<{
      /** What the merge changed. */
      merge: BundleMergeResult;
      /** Workspace after the merge, ready for the workspace store. */
      workspace: WorkspaceData;
    }>(`/workspace/bundles/${id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ intoBundleId }),
    }),
};

/** Dry-run answer of `POST /workspace/bundles/:id/merge`. */
export interface BundleMergePreview {
  /** Bundle whose activities would move. */
  source_bundle_id: string;
  /** Bundle that would receive them. */
  target_bundle_id: string;
  /** Every activity in the source, including completed ones. */
  activity_count: number;
  /** Activities in the source that are not completed or cancelled. */
  open_activity_count: number;
  /** Purpose the target would have after the merge, or null. */
  target_purpose: string | null;
  /** Whether the target would still need a purpose. */
  target_needs_purpose: boolean;
  /** Whether this pair was already merged; repeating it only moves stragglers. */
  already_merged: boolean;
}

/** Committed answer of `POST /workspace/bundles/:id/merge`. */
export interface BundleMergeResult {
  /** The archived source bundle. */
  source_bundle: WorkspaceBundle;
  /** The target bundle after the merge. */
  target_bundle: WorkspaceBundle;
  /** Number of activities that now belong to the target. */
  moved_activity_count: number;
  /** Identifiers of the moved activities. */
  moved_activity_ids: string[];
  /** Whether the pair had already been merged before this call. */
  already_merged: boolean;
}

/**
 * Personal values, value links, applications, value-yield suggestions and the Project/Bundle
 * structure tree. Server rules live in `apps/api-rs/src/values.rs`.
 */
export const valuesApi = {
  /** Every value and link the signed-in person has. */
  list: () =>
    request<{ /** Values, active first. */ values: UserValue[]; /** Links. */ links: ValueLink[] }>(
      '/values'
    ),
  /** Save a new value in the person's words. */
  create: (input: ValueInput) =>
    request<{ /** The saved value. */ value: UserValue }>('/values', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Edit or archive a value. */
  update: (id: string, input: ValueInput) =>
    request<{ /** The edited value. */ value: UserValue }>(`/values/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  /** Attach or detach a value and/or purpose. */
  link: (input: ValueLinkInput) =>
    request<{ /** The link afterwards, or null. */ link: ValueLink | null }>('/values/links', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  /** Apply a value to groups of activities, optionally changing their planned energy. */
  apply: (input: ApplyValueInput) =>
    request<{ /** What changed. */ application: ApplicationReceipt }>('/values/apply', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Undo one application. */
  undo: (applicationId: string) =>
    request<{ /** What was restored. */ undo: UndoReceipt }>(
      `/values/applications/${applicationId}/undo`,
      { method: 'POST', body: '{}' }
    ),
  /** Rank the person's values for one activity by predicted energy yield. */
  suggestions: (activityId: string) =>
    request<{ /** The ranking. */ suggestions: ValueSuggestions }>(
      `/values/suggestions?activityId=${encodeURIComponent(activityId)}`
    ),
  /** The Project/Bundle tree and what is untidy about it. */
  structure: () => request<StructureResponse>('/workspace/structure'),
  /** Draw a reorganization without writing it. */
  previewStructure: (operations: StructureOperation[]) =>
    request<StructureResponse>('/workspace/structure/preview', {
      method: 'POST',
      body: JSON.stringify({ operations }),
    }),
  /** Execute a confirmed reorganization. */
  applyStructure: (operations: StructureOperation[]) =>
    request<{ /** What was written. */ receipt: ReorganizeReceipt }>('/workspace/structure/apply', {
      method: 'POST',
      body: JSON.stringify({ operations }),
    }),
};

/**
 * Team workspace, governance, membership, projects, bundles, activities, fields, feeds, email rules, and connection API methods. Server authorization remains authoritative.
 */
export const teamsApi = {
  /** Load teams with the current account's membership information. */
  list: () => request<TeamWithMembership[]>('/teams'),
  /** Load discoverable teams and server-computed join/invitation actions. */
  discover: () => request<TeamDiscoveryResult[]>('/teams/discover'),
  /** Create a team workspace under the current session. */
  create: (team: TeamInput) =>
    request<{ /** The created team with the creator's membership. */ team: TeamWithMembership }>(
      '/teams',
      {
        method: 'POST',
        body: JSON.stringify(team),
      }
    ),
  /** Load a selected team's aggregate workspace after server membership checks. */
  get: (teamId: string) => request<TeamWorkspaceData>(`/teams/${teamId}`),
  /** Patch team identity, appearance, or policy fields subject to server authorization. */
  update: (teamId: string, updates: Partial<TeamInput>) =>
    request<{ /** The updated team. */ team: TeamWithMembership }>(`/teams/${teamId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  /** Submit a direct team-member invitation under the caller's permissions. */
  inviteMember: (teamId: string, member: MemberInviteInput) =>
    request<{
      /** The invited member. */ member: TeamMember;
      /** Invitation email outcome. */ invite_email: InviteDelivery;
      /** Mail-account outcome when one was requested, else null. */
      mail_provisioning: MailProvisioningOutcome | null;
    }>(`/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify(member),
    }),
  /** Submit an invitation proposal for governance review. */
  requestMemberInvite: (teamId: string, member: MemberInviteInput) =>
    request<{ /** The stored proposal. */ request: TeamInviteRequest }>(
      `/teams/${teamId}/member-invite-requests`,
      {
        method: 'POST',
        body: JSON.stringify(member),
      }
    ),
  /** Update the decision on an invitation proposal. */
  decideMemberInviteRequest: (teamId: string, requestId: string, updates: MemberDecisionInput) =>
    request<{
      /** Member created by an approval, else null. */ member: TeamMember | null;
      /** Invitation email outcome of an approval, else null. */ invite_email: InviteDelivery | null;
    }>(`/teams/${teamId}/member-invite-requests/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  /** Accept an eligible invitation using the supplied identity evidence. */
  acceptInvite: (
    teamId: string,
    payload: { /** Invited address to accept as. */ email?: string }
  ) =>
    request<{
      /** The accepted membership. */ member: TeamMember;
      /** Set when the account was already a member. */ already_member?: true;
    }>(`/teams/${teamId}/invitations/accept`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Patch a member's role or lifecycle state subject to server governance checks. */
  updateMember: (
    teamId: string,
    memberId: string,
    updates: {
      /** New role: `manager` or `member`. */ role?: string;
      /** New lifecycle state: `active`, `invited` or `removed`. */ status?: string;
    }
  ) =>
    request<void>(`/teams/${teamId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  /** Request mail-account provisioning for a selected member under server permission checks. */
  provisionMemberMailAccount: (teamId: string, memberId: string, payload: MailAccountRequest) =>
    request<{ /** Provisioning outcome. */ mail_provisioning: MailProvisioningOutcome }>(
      `/teams/${teamId}/members/${memberId}/mail-account`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    ),
  /** Submit a request to join the specified team. */
  requestJoin: (
    teamId: string,
    payload: {
      /** Address to join as. */ email?: string;
      /** Display name for the request. */ display_name?: string;
    }
  ) =>
    request<{
      /** Membership created by auto-approval. */ member?: TeamMember;
      /** Pending request, when review is needed. */ request?: TeamJoinRequest;
      /** Set when the account was already a member. */ already_member?: true;
      /** Set when a join rule approved the request immediately. */ auto_approved?: true;
    }>(`/teams/${teamId}/join-requests`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Record a governance decision on a pending join request. */
  updateJoinRequest: (teamId: string, requestId: string, updates: MemberDecisionInput) =>
    request<void>(`/teams/${teamId}/join-requests/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  /** Create an email-matching admission rule for the team. */
  createJoinRule: (teamId: string, rule: JoinRuleInput) =>
    request<{ /** The stored rule. */ rule: TeamJoinRule }>(`/teams/${teamId}/join-rules`, {
      method: 'POST',
      body: JSON.stringify(rule),
    }),
  /** Remove one team admission rule. */
  deleteJoinRule: (teamId: string, ruleId: string) =>
    request<void>(`/teams/${teamId}/join-rules/${ruleId}`, { method: 'DELETE' }),
  /** Create a shared project within the specified team. */
  createProject: (teamId: string, project: TeamProjectInput) =>
    request<{ /** The created project. */ project: WorkspaceProject }>(
      `/teams/${teamId}/projects`,
      {
        method: 'POST',
        body: JSON.stringify(project),
      }
    ),
  /** Patch a shared project's fields within the selected team scope. */
  updateProject: (teamId: string, projectId: string, project: Partial<TeamProjectInput>) =>
    request<{ /** The updated project. */ project: WorkspaceProject }>(
      `/teams/${teamId}/projects/${projectId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(project),
      }
    ),
  /** Delete a shared project under server team authorization. */
  deleteProject: (teamId: string, projectId: string) =>
    request<DeletedRecord>(`/teams/${teamId}/projects/${projectId}`, { method: 'DELETE' }),
  /** Create a shared activity bundle within the specified team. */
  createBundle: (teamId: string, bundle: TeamBundleInput) =>
    request<{ /** The created bundle. */ bundle: WorkspaceBundle }>(`/teams/${teamId}/bundles`, {
      method: 'POST',
      body: JSON.stringify(bundle),
    }),
  /** Patch a shared bundle's fields within the selected team scope. */
  updateBundle: (teamId: string, bundleId: string, bundle: Partial<TeamBundleInput>) =>
    request<{ /** The updated bundle. */ bundle: WorkspaceBundle }>(
      `/teams/${teamId}/bundles/${bundleId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(bundle),
      }
    ),
  /** Delete a shared bundle under server team authorization. */
  deleteBundle: (teamId: string, bundleId: string) =>
    request<DeletedRecord>(`/teams/${teamId}/bundles/${bundleId}`, { method: 'DELETE' }),
  /** Create an activity assigned to the team's workspace and selected member. */
  createActivity: (teamId: string, activity: TeamActivityInput) =>
    request<{ /** The created shared activity. */ task: TaskDocument }>(
      `/teams/${teamId}/activities`,
      {
        method: 'POST',
        body: JSON.stringify(activity),
      }
    ),
  /** Create a shared field definition in the selected team schema. */
  createAttribute: (teamId: string, attribute: AttributeInput) =>
    request<{ /** The stored field. */ attribute: TeamCustomField }>(
      `/teams/${teamId}/attributes`,
      {
        method: 'POST',
        body: JSON.stringify(attribute),
      }
    ),
  /** Patch a shared field definition under server schema validation. */
  updateAttribute: (teamId: string, attributeId: string, attribute: Partial<AttributeInput>) =>
    request<{ /** The updated field. */ attribute: TeamCustomField }>(
      `/teams/${teamId}/attributes/${attributeId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(attribute),
      }
    ),
  /** Delete one shared field definition under team authorization. */
  deleteAttribute: (teamId: string, attributeId: string) =>
    request<void>(`/teams/${teamId}/attributes/${attributeId}`, { method: 'DELETE' }),
  /** Create a rule that converts matching mail into activities or proposals. */
  createEmailRule: (teamId: string, rule: TeamEmailRuleInput) =>
    request<{ /** The stored rule. */ rule: TeamEmailRule }>(`/teams/${teamId}/email-rules`, {
      method: 'POST',
      body: JSON.stringify(rule),
    }),
  /** Remove a team email-matching rule. */
  deleteEmailRule: (teamId: string, ruleId: string) =>
    request<void>(`/teams/${teamId}/email-rules/${ruleId}`, { method: 'DELETE' }),
  /** Create a team subscription feed with the supplied disclosure settings. */
  createFeed: (teamId: string, feed: TeamFeedInput) =>
    request<{ /** The created feed; `url` is present only now. */ feed: TeamFeed }>(
      `/teams/${teamId}/feeds`,
      {
        method: 'POST',
        body: JSON.stringify(feed),
      }
    ),
  /** Revoke a subscription feed by its stored identifier. */
  revokeFeed: (teamId: string, feedId: string) =>
    request<void>(`/teams/${teamId}/feeds/${feedId}`, { method: 'DELETE' }),
  /** Create an external provider connection for the selected team. */
  createConnection: (teamId: string, connection: TeamConnectionInput) =>
    request<{ /** The stored connection. */ connection: TeamExternalConnection }>(
      `/teams/${teamId}/connections`,
      {
        method: 'POST',
        body: JSON.stringify(connection),
      }
    ),
  /** Remove a selected team provider connection. */
  deleteConnection: (teamId: string, connectionId: string) =>
    request<void>(`/teams/${teamId}/connections/${connectionId}`, { method: 'DELETE' }),
};

// User API
/**
 * Current-user profile, preferences, onboarding, planning constraints, custom attributes, and verified-email management methods.
 */
export const userApi = {
  /** Call the retained registration path with a fixed anonymous-password placeholder; this legacy helper is not proof of account authentication. */
  create: (user: {
    /** Account email. */ email: string;
    /** Display name. */ displayName: string;
  }) =>
    request<AccountResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email: user.email,
        password: 'anonymous-password', // Dummy password for anonymous users
        displayName: user.displayName,
      }),
    }),
  /** Load the authoritative current-user profile. */
  getProfile: () => request<AccountResponse>('/user/me'),
  /** Load linked account addresses and provider metadata. */
  getEmails: () => request<EmailList>('/user/me/emails'),
  /** Load outstanding email-verification requests. */
  getPendingEmails: () =>
    request<{ /** Addresses awaiting verification. */ pending: PendingEmail[] }>(
      '/user/me/emails/pending'
    ),
  /** Request verification of an additional email address. */
  linkEmail: (payload: { email: string }) =>
    request<EmailLinkResult>('/user/me/emails/link', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Submit the email-verification token for server validation and linking. */
  verifyEmail: (payload: { token: string }) =>
    request<EmailVerifyResult>('/user/me/emails/verify', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Read the address-change details associated with a verification token before confirmation. */
  previewEmailVerification: (payload: { token: string }) =>
    request<EmailVerifyPreview>('/user/me/emails/verify/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Patch current-account profile fields. */
  updateProfile: (updates: ProfileUpdate) =>
    request<AccountResponse>('/user/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  /** Replace the profile picture with a PNG, JPEG or WebP blob; the server answers with the new `avatar_url`. */
  uploadAvatar: (image: Blob) =>
    request<{ /** Versioned picture path under the API root. */ avatar_url: string }>(
      '/user/me/avatar',
      {
        method: 'PUT',
        headers: { 'Content-Type': image.type || 'image/png' },
        body: image,
      }
    ),
  /** Remove the profile picture; succeeds when there is none. */
  removeAvatar: () =>
    request<{ /** Always null after removal. */ avatar_url: null }>('/user/me/avatar', {
      method: 'DELETE',
    }),
  /** Load persisted planner preferences. */
  getSettings: () => request<SettingsResponse>('/user/me/settings'),
  /** Submit replacement planner preference data to the settings endpoint. */
  updateSettings: (settings: SettingsUpdate) =>
    request<Pick<SettingsResponse, 'settings'>>('/user/me/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  /** Persist onboarding responses and completion state. */
  completeOnboarding: (payload: OnboardingInput) =>
    request<SettingsResponse>('/user/me/onboarding', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Load personal work-hours, break, and scheduling rules. */
  getConstraints: () =>
    request<{ /** Stored constraints. */ constraints: SchedulingConstraint[] }>(
      '/user/me/constraints'
    ),
  /** Create a personal scheduling constraint. */
  addConstraint: (constraint: ConstraintInput) =>
    request<{
      /** The stored constraint. */ constraint: SchedulingConstraint;
      /** Set when the request replayed an earlier one. */ idempotent_replay?: true;
    }>('/user/me/constraints', {
      method: 'POST',
      body: JSON.stringify(constraint),
    }),
  /** Remove a scheduling constraint by numeric identifier. */
  deleteConstraint: (id: number) =>
    request<{ /** Confirmation text. */ message: string }>(`/user/me/constraints/${id}`, {
      method: 'DELETE',
    }),
  /** Load personal field definitions available to activity editors. */
  getAttributes: () => request<AttributeDef[]>('/attributes'),
  /** Create a personal field definition. */
  addAttribute: (attr: AttributeInput) =>
    request<{ /** Identifier of the new field. */ attr_id: string }>('/attributes', {
      method: 'POST',
      body: JSON.stringify(attr),
    }),
  /** Submit an updated personal field definition through the retained PUT endpoint. */
  updateAttribute: (id: string, attr: Partial<AttributeInput>) =>
    request<void>(`/attributes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(attr),
    }),
  /** Delete a personal field definition by string identifier. */
  deleteAttribute: (id: string) => request<void>(`/attributes/${id}`, { method: 'DELETE' }),
};

// Auth API
/**
 * Session lookup, provider-token exchange, email linking, and logout methods used by AuthProvider.
 */
export const authApi = {
  /** Exchange a FluxAccounts or accepted legacy issuer token for a planner session. */
  saidExchange: (data: { token: string }) =>
    request<
      AccountResponse & {
        /** Legacy bearer token. Current servers set the session cookie and omit it. */
        token?: string;
      }
    >('/auth/said/exchange', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** Submit email/password credentials to the retained planner login endpoint. */
  login: (credentials: { email: string; password: string }) =>
    request<AccountResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
  /** Submit identifier/password credentials to the retained Sahast compatibility endpoint. */
  sahastLogin: (credentials: { identifier: string; password: string }) =>
    request<AccountResponse>('/auth/sahast/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
  /** Exchange a mail bridge token for a planner session. */
  mailExchange: (data: { token: string }) =>
    request<
      AccountResponse & {
        /** Mailbox the bridge token identified. */
        mail_account: {
          /** Mail domain. */ domain: string;
          /** Mail-system user ID. */ mail_user_id: string;
          /** Mailbox address. */ email: string;
          /** Mail role. */ role: string;
        };
      }
    >('/auth/mail/exchange', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** Load configured OAuth-provider availability for the sign-in UI. */
  oauthProviders: () => request<OAuthProviders>('/auth/oauth/providers'),
  /** Request a Zoho sign-in URL with an encoded local return destination. */
  zohoAuthUrl: (returnTo = '/teams') =>
    request<{ url: string }>(`/auth/zoho/url?return_to=${encodeURIComponent(returnTo)}`),
  /** Request a Zoho identity-linking URL for the existing account. */
  zohoLinkAuthUrl: (returnTo = '/settings') =>
    request<{ url: string }>(`/auth/zoho/link-url?return_to=${encodeURIComponent(returnTo)}`),
  /** Request a GitHub sign-in URL with an encoded local return destination. */
  githubAuthUrl: (returnTo = '/teams') =>
    request<{ url: string }>(`/auth/github/url?return_to=${encodeURIComponent(returnTo)}`),
  /** Request a GitHub identity-linking URL for the existing account. */
  githubLinkAuthUrl: (returnTo = '/settings') =>
    request<{ url: string }>(`/auth/github/link-url?return_to=${encodeURIComponent(returnTo)}`),
  /** Submit explicit email/password registration and a display name. */
  register: (data: { email: string; password: string; displayName: string }) =>
    request<AccountResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** Invalidate the planner session; the caller clears local identity after inspecting the result. */
  logout: () =>
    request<void>('/auth/logout', {
      method: 'POST',
    }),
  /** Verify the current planner session and return its identity projection. */
  session: () => request<AccountResponse>('/auth/session'),
};

// Combined API export
/**
 * Grouped API facade shared by React screens, stores, and planning helpers.
 */
export const api = {
  /** Activity CRUD and planning-field inference facade. */
  tasks: tasksApi,
  /** Placement, capacity, ghost confirmation, and asynchronous solve facade. */
  schedule: scheduleApi,
  /** Natural-language command and suggestion facade. */
  nlp: nlpApi,
  /** Provider calendar authorization, selection, synchronization, and commitment facade. */
  calendar: calendarApi,
  /** Image scanning and reviewed activity creation facade. */
  ocr: ocrApi,
  /** Work sessions, feedback, analytics, and evidence export facade. */
  training: trainingApi,
  /** Hydration, sleep, subjective check-ins, and trajectory facade. */
  state: stateApi,
  /** Personal project and bundle facade. */
  workspace: workspaceApi,
  /** Personal values, links, applications, suggestions and the structure tree. */
  values: valuesApi,
  /** Shared workspace and governance facade; server permission checks remain authoritative. */
  teams: teamsApi,
  /** Current profile, preferences, custom fields, and verified-address facade. */
  user: userApi,
  /** Session lookup, provider exchange, and authentication facade. */
  auth: authApi,
};
